import { getScheduleRecordId } from "library/get-unique-id-from-tree.mjs"

/**
 * Schedule assignments (FIQ-2457) route a whole schedule library folder, a schedule or a
 * single task to a trade. They sit above skill routing: when one applies it wins outright,
 * because the customer's model is "this whole schedule goes to this contractor".
 */
export const ASSIGNMENT_KINDS = ["folder", "schedule", "task"]

/**
 * Target id for a task assignment. Keyed on the schedule record id (not the regime copy)
 * so the assignment follows the task into any regime and across schedule versions.
 *
 * @param {string} scheduleRecordId
 * @param {string} taskId - The schedule's own task id (task.task.id on a regime task)
 * @returns {string}
 */
export function taskAssignmentTargetId(scheduleRecordId, taskId) {
    return `${scheduleRecordId}:${taskId}`
}

/**
 * Split a task assignment's target id back into its schedule and task. Schedule record ids
 * contain a colon themselves (e.g. "292026:sfg20/schedule"), so only the last colon
 * separates the task id.
 *
 * @param {string} targetId - From taskAssignmentTargetId
 * @returns {{ scheduleId: string, taskId: string }}
 */
export function splitTaskAssignmentTargetId(targetId) {
    const value = String(targetId)
    const separator = value.lastIndexOf(":")
    if (separator < 0) return { scheduleId: value, taskId: "" }
    return { scheduleId: value.slice(0, separator), taskId: value.slice(separator + 1) }
}

/**
 * Pick the folder assignment that routes a schedule. A schedule can be filed in several
 * places in the library (e.g. under both Fire Protection Systems and Signage), so this
 * looks at every placement: the deepest assigned folder wins, and between folders at the
 * same depth the placement listed first in the library wins. The board and the cost
 * engine both route through this, so they always agree.
 *
 * @param {string[][]} placements - Each placement's folder ids, root first, in library order
 * @param {(folderId: string) => (object | undefined)} folderAssignmentFor
 * @returns {object | null} The winning folder assignment
 */
export function pickFolderAssignment(placements, folderAssignmentFor) {
    let best = null
    let bestDepth = -1
    for (const folderIds of placements || []) {
        for (let depth = folderIds.length - 1; depth > bestDepth; depth--) {
            const assignment = folderAssignmentFor(folderIds[depth])
            if (assignment) {
                best = assignment
                bestDepth = depth
                break
            }
        }
    }
    return best
}

/**
 * Build the per-run lookup the cost engine consults for every task. Assignments are
 * expanded here once, and folder resolution is memoised per schedule, so resolving a
 * task costs a couple of Map lookups (the trade resolver is a benchmarked hot path).
 *
 * Precedence, most specific first: task, schedule, then the deepest assigned folder.
 * Assignments pointing at a trade that is no longer on the skill map are dropped, so a
 * delete racing a calculation falls back to skill routing instead of a ghost trade.
 *
 * @param {Array<{ _id: string, kind: string, targetId: string, tradeId: string, name?: string }>} assignments
 * @param {{ tradeNames: object, scheduleLibraryPlacements?: Map<string, string[][]> }} options
 *   scheduleLibraryPlacements maps a schedule record id to each place it is filed in the
 *   library, as folder ids root first (see buildScheduleLibraryPlacements).
 * @returns {{ resolve: (task: object) => ({ tradeId: string, assignmentMatched: object } | null) } | null}
 *   null when nothing is assigned, so callers can skip the lookup entirely.
 */
export function buildScheduleAssignmentIndex(assignments, { tradeNames, scheduleLibraryPlacements } = {}) {
    const byKind = { folder: new Map(), schedule: new Map(), task: new Map() }
    for (const assignment of assignments || []) {
        // Own keys only: a tradeId such as "constructor" must not match Object.prototype
        if (!assignment?.tradeId || !Object.hasOwn(tradeNames ?? {}, assignment.tradeId)) continue
        byKind[assignment.kind]?.set(String(assignment.targetId), assignment)
    }
    if (!byKind.folder.size && !byKind.schedule.size && !byKind.task.size) return null

    const bySchedule = new Map()
    return { resolve }

    function resolve(task) {
        const scheduleId = scheduleRecordIdForTask(task)
        if (!scheduleId) return null
        if (byKind.task.size) {
            const taskAssignment = byKind.task.get(taskAssignmentTargetId(scheduleId, task.task?.id))
            if (taskAssignment) return toMatch(taskAssignment)
        }
        if (!bySchedule.has(scheduleId)) bySchedule.set(scheduleId, resolveSchedule(scheduleId))
        return bySchedule.get(scheduleId)
    }

    function resolveSchedule(scheduleId) {
        const scheduleAssignment = byKind.schedule.get(scheduleId)
        if (scheduleAssignment) return toMatch(scheduleAssignment)
        if (!byKind.folder.size) return null
        const folderAssignment = pickFolderAssignment(scheduleLibraryPlacements?.get(scheduleId), folderAssignmentFor)
        return folderAssignment ? toMatch(folderAssignment) : null
    }

    function folderAssignmentFor(folderId) {
        return byKind.folder.get(folderId)
    }
}

/**
 * The schedule record id a regime task is keyed on, for assignments and library placements.
 * The board (scheduleAssignmentRegime) and the cost engine both key tasks through this, so
 * they agree on which schedule a task belongs to.
 *
 * @param {object} task - A regime task from getTasksForRegimeLean
 * @returns {string | null}
 */
export function scheduleRecordIdForTask(task) {
    const raw = task?.schedule?._id ?? task?.scheduleId
    return raw ? getScheduleRecordId(String(raw)) : null
}

function toMatch(assignment) {
    return {
        tradeId: assignment.tradeId,
        assignmentMatched: {
            id: assignment._id ?? assignment.id,
            kind: assignment.kind,
            name: assignment.name ?? "",
        },
    }
}
